/**
 * useWelcomeIntent — состояние приветственного выбора на главной.
 *
 * UX-контракт (согласовано):
 *   1. Кнопки Текст/Изображение/Видео/Аудио/Презентации показываются
 *      пока intent не выбран в текущей session.
 *   2. До выбора кнопка отправки в textarea неактивна.
 *   3. После клика по intent — кнопки скрываются, чтобы освободить место
 *      под textarea и подсказку выбранного типа.
 *   4. На F5 в той же вкладке выбор сохраняется (sessionStorage).
 *      Новая вкладка / новая сессия → выбор сбрасывается.
 *
 * Загрузка дефолтных model_id для каждой категории — публичный endpoint
 * GET /api/v1/welcome-config. Если ключ не задан в platform_settings,
 * default_models[category] будет null — кнопка всё равно видна, но
 * перед роутингом проверяем что model_id выбран в админке.
 */
import { useCallback, useEffect, useState } from 'react'

const API_BASE = import.meta.env.VITE_API_URL || ''
const SESSION_STORAGE_KEY = 'umnik.welcome.selected_intent'

// Same-tab DOM event для синхронизации `selected` между компонентами,
// которые читают sessionStorage напрямую (Header через useSelectedWelcomeIntent)
// и тем, который пишет (HomeTemp через useWelcomeIntent.selectIntent).
// `storage` event срабатывает только в ДРУГИХ табах — для same-tab нужно
// явное событие. Без него Header не увидит обновление до перезагрузки.
const SELECTED_CHANGED_EVENT = 'umnik:welcome-intent-changed'

export type IntentType = 'text' | 'image' | 'video' | 'audio' | 'presentation'

export interface WelcomeIntent {
	type: IntentType
	label: string
	// lucide-react имя иконки. Резолвится в WelcomeIntentButtons через
	// LucideIcons[name] (no fallback). Если переименуется на бэке —
	// ловится здесь, не в админке.
	iconName: string
	// Куда вести после клика. /chat и /generate остаются на user-стэке;
	// /bots/audio и /bots/slides идут в bots-виджеты.
	targetRoute: '/chat' | '/generate' | '/bots/audio' | '/bots/tools/slides'
}

export interface SelectedIntent {
	type: IntentType
	modelId: string | null
}

interface WelcomeConfigResponse {
	ok: boolean
	default_models: Partial<Record<IntentType, string | null>>
}

// Статика — лейблы и роуты не зависят от админки. Подсветить маршрут в
// одном месте, чтобы не дублировать switch/case в HomeTemp.
const INTENT_DEFINITIONS: Omit<WelcomeIntent, never>[] = [
	{ type: 'text',         label: 'Текст',         iconName: 'MessageSquare', targetRoute: '/chat' },
	{ type: 'image',        label: 'Изображение',   iconName: 'Image',         targetRoute: '/generate' },
	{ type: 'video',        label: 'Видео',         iconName: 'Video',         targetRoute: '/generate' },
	{ type: 'audio',        label: 'Аудио',         iconName: 'Music',         targetRoute: '/bots/audio' },
	{ type: 'presentation', label: 'Презентации',   iconName: 'Presentation',  targetRoute: '/bots/tools/slides' },
]

function loadFromSession(): SelectedIntent | null {
	try {
		const raw = sessionStorage.getItem(SESSION_STORAGE_KEY)
		if (!raw) return null
		const parsed = JSON.parse(raw)
		if (parsed && typeof parsed.type === 'string') return parsed as SelectedIntent
		return null
	} catch {
		return null
	}
}

interface UseWelcomeIntentReturn {
	intents: WelcomeIntent[]
	defaultModels: Partial<Record<IntentType, string | null>>
	selected: SelectedIntent | null
	loading: boolean
	selectIntent: (type: IntentType, modelId?: string | null) => SelectedIntent
	clearIntent: () => void
}

export function useWelcomeIntent(): UseWelcomeIntentReturn {
	const [defaultModels, setDefaultModels] = useState<
		Partial<Record<IntentType, string | null>>
	>({})
	const [loading, setLoading] = useState(true)
	const [selected, setSelected] = useState<SelectedIntent | null>(loadFromSession)

	useEffect(() => {
		let cancelled = false
		// Initial useState(true) уже задаёт loading=true до завершения fetch,
		// явный setLoading(true) тут избыточен и триггерил каскадный рендер.
		fetch(`${API_BASE}/api/v1/welcome-config`)
			.then((r) => r.json() as Promise<WelcomeConfigResponse>)
			.then((data) => {
				if (cancelled) return
				if (data.ok) setDefaultModels(data.default_models || {})
			})
			.catch(() => {
				// Бэк недоступен — оставляем пустую мапу. Кнопки всё равно
				// показываются, но selectIntent отдаст modelId=null и
				// HomeTemp сам решит как реагировать (роутинг для audio/
				// presentation работает и без модели через last-used map).
			})
			.finally(() => {
				if (!cancelled) setLoading(false)
			})
		return () => {
			cancelled = true
		}
	}, [])

	const selectIntent = useCallback(
		(type: IntentType, modelId?: string | null): SelectedIntent => {
			const next: SelectedIntent = {
				type,
				// Модель кликнутой плитки приоритетнее дефолта категории: в ряду
				// бот-селектора может быть несколько плиток одной категории с
				// разными моделями (config.tiles секции).
				modelId: modelId ?? defaultModels[type] ?? null,
			}
			setSelected(next)
			try {
				sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(next))
			} catch {
				// QuotaExceeded — игнорим.
			}
			// Broadcast: Header и любые другие компоненты, использующие
			// useSelectedWelcomeIntent, подпишутся и обновят свой стейт без
			// перезагрузки. Без этого ModelSelector в шапке остаётся пустым
			// до refresh страницы после клика по welcome-плитке.
			window.dispatchEvent(new CustomEvent(SELECTED_CHANGED_EVENT))
			return next
		},
		[defaultModels],
	)

	const clearIntent = useCallback(() => {
		setSelected(null)
		try {
			sessionStorage.removeItem(SESSION_STORAGE_KEY)
		} catch {
			// игнорим
		}
		window.dispatchEvent(new CustomEvent(SELECTED_CHANGED_EVENT))
	}, [])

	return {
		intents: INTENT_DEFINITIONS,
		defaultModels,
		selected,
		loading,
		selectIntent,
		clearIntent,
	}
}

/**
 * Lightweight-чтение `selected` без fetch'а к /welcome-config. Использовать
 * там, где нужно только знать «кликнул ли юзер welcome-плитку», без
 * default_models — например, в Header'е для условного показа ModelSelector
 * с выбранной моделью.
 *
 * Полный useWelcomeIntent делает fetch к /welcome-config на mount —
 * это нужно HomeTemp для рендера плиток, но Header'у такой запрос лишний
 * (он рендерится на всех страницах). useSelectedWelcomeIntent читает
 * только sessionStorage + слушает same-tab event для синхронизации с
 * useWelcomeIntent.selectIntent.
 */
export function useSelectedWelcomeIntent(): SelectedIntent | null {
	const [selected, setSelected] = useState<SelectedIntent | null>(loadFromSession)

	useEffect(() => {
		const reload = () => setSelected(loadFromSession())
		// Same-tab event от useWelcomeIntent.selectIntent/clearIntent.
		window.addEventListener(SELECTED_CHANGED_EVENT, reload as EventListener)
		// Cross-tab event (юзер открыл главную в другой вкладке).
		window.addEventListener('storage', reload)
		return () => {
			window.removeEventListener(SELECTED_CHANGED_EVENT, reload as EventListener)
			window.removeEventListener('storage', reload)
		}
	}, [])

	return selected
}
